import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useParams, useLocation } from 'wouter';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  ArrowLeft,
  AlertCircle,
  Edit,
  MoreHorizontal,
  Trash2,
  Archive,
  ArchiveRestore,
  Plus,
  Pencil,
  CreditCard,
  Loader2,
  CalendarIcon,
  ShoppingCart,
  User,
  FileText,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { StatusBadge } from '@/components/StatusBadge';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { FormDrawer } from '@/components/ui/form-drawer';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useToast } from '@/hooks/use-toast';
import { apiRequest } from '@/lib/api';
import { cn } from '@/lib/cn';

const formatCurrency = (value: string | number | null | undefined) => {
  const num = Number(value || 0);
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(num);
};

const formatDate = (date: string | null | undefined) => {
  if (!date) return '—';
  return new Date(date).toLocaleDateString();
};

interface InvoiceLineItem {
  id: string;
  description: string;
  quantity: number;
  uom: string;
  unit_price: string;
  line_total: string;
}

interface Invoice {
  id: string;
  invoice_number: string;
  account_id: string;
  order_id: string | null;
  status: string;
  invoice_date: string;
  due_date: string | null;
  payment_terms: string | null;
  notes: string | null;
  subtotal: string;
  total: string;
  amount_paid: string;
  balance: string;
  created_at: string;
  account?: { name: string };
  order?: { orderNumber: string };
  createdBy?: { firstName: string; lastName: string };
  lineItems?: InvoiceLineItem[];
}

interface PaymentRecord {
  id: string;
  payment_date: string;
  applied_amount: string;
  payment_method: string | null;
  reference_number: string | null;
}

const invoiceFormSchema = z.object({
  status: z.string().min(1, 'Status is required'),
  dueDate: z.date().nullable().optional(),
  paymentTerms: z.string().optional(),
  notes: z.string().optional(),
});

const lineItemFormSchema = z.object({
  description: z.string().min(1, 'Description is required'),
  quantity: z.coerce.number().min(0.01, 'Quantity must be positive'),
  uom: z.string().default('each'),
  unitPrice: z.coerce.number().min(0, 'Unit price must be non-negative'),
});

const paymentFormSchema = z.object({
  paymentDate: z.date(),
  amount: z.coerce.number().min(0.01, 'Amount must be positive'),
  paymentMethod: z.string().optional(),
  referenceNumber: z.string().optional(),
});

export default function InvoiceDetailPage() {
  const params = useParams<{ id: string }>();
  const [, setLocation] = useLocation();
  const { toast } = useToast();

  const [editOpen, setEditOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [lineItemDialogOpen, setLineItemDialogOpen] = useState(false);
  const [editingLineItem, setEditingLineItem] = useState<InvoiceLineItem | null>(null);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const queryClient = useQueryClient();

  const isNew = params.id === 'new';

  const { data: invoice, isLoading, error } = useQuery<Invoice>({
    queryKey: ['/api/invoices', params.id],
    enabled: !isNew,
  });

  const { data: payments = [] } = useQuery<PaymentRecord[]>({
    queryKey: ['/api/invoices', params.id, 'payments'],
    enabled: !isNew && !!params.id,
  });

  const form = useForm({
    resolver: zodResolver(invoiceFormSchema),
    defaultValues: { status: 'draft', dueDate: null, paymentTerms: '', notes: '' },
  });

  const lineItemForm = useForm({
    resolver: zodResolver(lineItemFormSchema),
    defaultValues: { description: '', quantity: 1, uom: 'each', unitPrice: 0 },
  });

  const paymentForm = useForm({
    resolver: zodResolver(paymentFormSchema),
    defaultValues: { paymentDate: new Date(), amount: 0, paymentMethod: '', referenceNumber: '' },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: any) => {
      return apiRequest('PATCH', `/api/invoices/${params.id}`, {
        ...data,
        dueDate: data.dueDate?.toISOString() || null,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/invoices', params.id] });
      queryClient.invalidateQueries({ queryKey: ['/api/invoices'] });
      toast({ title: 'Invoice updated' });
      setEditOpen(false);
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => apiRequest('DELETE', `/api/invoices/${params.id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/invoices'] });
      toast({ title: 'Invoice deleted' });
      setLocation('/invoices');
    },
  });

  const archiveMutation = useMutation({
    mutationFn: async () => apiRequest('POST', `/api/invoices/${params.id}/archive`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/invoices', params.id] });
      queryClient.invalidateQueries({ queryKey: ['/api/invoices'] });
      toast({ title: 'Invoice archived' });
      setArchiveDialogOpen(false);
    },
  });

  const unarchiveMutation = useMutation({
    mutationFn: async () => apiRequest('POST', `/api/invoices/${params.id}/unarchive`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/invoices', params.id] });
      queryClient.invalidateQueries({ queryKey: ['/api/invoices'] });
      toast({ title: 'Invoice restored' });
    },
  });

  const lineItemMutation = useMutation({
    mutationFn: async (data: any) => {
      if (editingLineItem) {
        return apiRequest('PUT', `/api/invoices/${params.id}/line-items/${editingLineItem.id}`, data);
      } else {
        return apiRequest('POST', `/api/invoices/${params.id}/line-items`, data);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/invoices', params.id] });
      toast({ title: editingLineItem ? 'Line item updated' : 'Line item added' });
      setLineItemDialogOpen(false);
      setEditingLineItem(null);
      lineItemForm.reset();
    },
  });

  const deleteLineItemMutation = useMutation({
    mutationFn: async (lineItemId: string) => {
      return apiRequest('DELETE', `/api/invoices/${params.id}/line-items/${lineItemId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/invoices', params.id] });
      toast({ title: 'Line item removed' });
    },
  });

  const paymentMutation = useMutation({
    mutationFn: async (data: any) => {
      return apiRequest('POST', `/api/invoices/${params.id}/payments`, {
        paymentDate: data.paymentDate.toISOString(),
        amount: data.amount.toString(),
        paymentMethod: data.paymentMethod || null,
        referenceNumber: data.referenceNumber || null,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/invoices', params.id] });
      queryClient.invalidateQueries({ queryKey: ['/api/invoices', params.id, 'payments'] });
      toast({ title: 'Payment applied' });
      setPaymentDialogOpen(false);
      paymentForm.reset();
    },
  });

  const handleEditOpen = () => {
    if (invoice) {
      form.reset({
        status: invoice.status || 'draft',
        dueDate: invoice.due_date ? new Date(invoice.due_date) : null,
        paymentTerms: invoice.payment_terms || '',
        notes: invoice.notes || '',
      });
      setEditOpen(true);
    }
  };

  const handleOpenLineItemDialog = (lineItem?: InvoiceLineItem) => {
    if (lineItem) {
      setEditingLineItem(lineItem);
      lineItemForm.reset({
        description: lineItem.description,
        quantity: lineItem.quantity,
        uom: lineItem.uom,
        unitPrice: Number(lineItem.unit_price),
      });
    } else {
      setEditingLineItem(null);
      lineItemForm.reset();
    }
    setLineItemDialogOpen(true);
  };

  const handleOpenPaymentDialog = () => {
    const balance = Number(invoice?.balance || 0);
    paymentForm.reset({
      paymentDate: new Date(),
      amount: balance > 0 ? balance : 0,
      paymentMethod: '',
      referenceNumber: '',
    });
    setPaymentDialogOpen(true);
  };

  if (isNew) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <AlertCircle className="h-12 w-12 text-muted-foreground" />
        <p className="text-lg font-semibold">New Invoice Creation</p>
        <p className="text-muted-foreground text-center max-w-md">
          Invoice creation UI is not yet implemented.
        </p>
        <Button variant="outline" onClick={() => setLocation('/invoices')}>
          Back to Invoices
        </Button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid gap-3 lg:grid-cols-3">
          <Skeleton className="h-[140px]" />
          <Skeleton className="h-[140px]" />
          <Skeleton className="h-[140px]" />
        </div>
      </div>
    );
  }

  if (error || !invoice) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <AlertCircle className="h-12 w-12 text-muted-foreground" />
        <p className="text-muted-foreground">Invoice not found</p>
        <Button variant="outline" onClick={() => setLocation('/invoices')}>
          Back to Invoices
        </Button>
      </div>
    );
  }

  const lineItems = invoice.lineItems || [];
  const isOverdue = invoice.due_date && new Date(invoice.due_date) < new Date() && !['paid', 'closed', 'voided'].includes(invoice.status);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <button onClick={() => setLocation('/invoices')} className="text-muted-foreground hover:text-foreground transition-colors">
              <ArrowLeft className="h-5 w-5" />
            </button>
            <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2 flex-wrap">
              <span className="text-muted-foreground font-normal">Invoice #:</span>
              <span>{invoice.invoice_number}</span>
              {invoice.account?.name && (
                <>
                  <span className="text-muted-foreground font-normal">/</span>
                  <span className="text-blue-600 dark:text-blue-400">{invoice.account.name}</span>
                </>
              )}
              {isOverdue && <Badge variant="destructive">Overdue</Badge>}
            </h1>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <div className="inline-flex h-9 items-center gap-1.5 rounded-md border bg-card px-3 shadow-sm text-sm">
            <span className="text-muted-foreground">Status:</span>
            <StatusBadge status={invoice.status} type="invoice" />
          </div>
          <Button variant="outline" size="sm" onClick={handleEditOpen}>
            <Edit className="h-3.5 w-3.5 mr-1.5" />
            Edit
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <MoreHorizontal className="h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {invoice.status !== 'archived' && (
                <DropdownMenuItem onClick={() => setArchiveDialogOpen(true)}>
                  <Archive className="h-4 w-4 mr-2" />
                  Archive
                </DropdownMenuItem>
              )}
              {invoice.status === 'archived' && (
                <DropdownMenuItem onClick={() => unarchiveMutation.mutate()}>
                  <ArchiveRestore className="h-4 w-4 mr-2" />
                  Unarchive
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={() => setDeleteDialogOpen(true)} className="text-destructive">
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Info Cards */}
      <div className="grid gap-4 lg:grid-cols-3">
        <div className="bg-card rounded-lg border p-4">
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <CalendarIcon className="h-4 w-4 text-muted-foreground" />
            Invoice Details
          </h3>
          <div className="space-y-3 text-sm">
            <div>
              <p className="text-xs text-muted-foreground mb-1">Invoice Date</p>
              <p className="font-medium">{formatDate(invoice.invoice_date)}</p>
            </div>
            {invoice.due_date && (
              <div>
                <p className="text-xs text-muted-foreground mb-1">Due Date</p>
                <p className={cn('font-medium', isOverdue && 'text-red-600 dark:text-red-500')}>
                  {formatDate(invoice.due_date)}
                </p>
              </div>
            )}
            {invoice.payment_terms && (
              <div>
                <p className="text-xs text-muted-foreground mb-1">Payment Terms</p>
                <p className="font-medium">{invoice.payment_terms}</p>
              </div>
            )}
            {invoice.order?.orderNumber && (
              <div>
                <p className="text-xs text-muted-foreground mb-1">Order</p>
                <button
                  onClick={() => setLocation(`/orders/${invoice.order_id}`)}
                  className="text-blue-600 dark:text-blue-400 hover:underline font-medium text-sm flex items-center gap-1.5"
                >
                  <ShoppingCart className="h-3.5 w-3.5" />
                  {invoice.order.orderNumber}
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="bg-card rounded-lg border p-4">
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <CreditCard className="h-4 w-4 text-muted-foreground" />
            Financial Summary
          </h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Subtotal</span>
              <span className="font-medium">{formatCurrency(invoice.subtotal)}</span>
            </div>
            <div className="flex justify-between border-t pt-2">
              <span className="font-semibold">Total</span>
              <span className="font-bold text-primary">{formatCurrency(invoice.total)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Paid</span>
              <span className="font-medium text-emerald-600 dark:text-emerald-400">
                {formatCurrency(invoice.amount_paid)}
              </span>
            </div>
            <div className="flex justify-between border-t pt-2">
              <span className="font-semibold">Balance Due</span>
              <span
                className={cn(
                  'font-bold',
                  Number(invoice.balance || 0) > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-muted-foreground'
                )}
              >
                {formatCurrency(invoice.balance)}
              </span>
            </div>
          </div>
        </div>

        <div className="bg-card rounded-lg border p-4">
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <User className="h-4 w-4 text-muted-foreground" />
            Additional Info
          </h3>
          <div className="space-y-3 text-sm">
            {invoice.createdBy && (
              <div>
                <p className="text-xs text-muted-foreground mb-1">Created By</p>
                <p className="font-medium">
                  {invoice.createdBy.firstName} {invoice.createdBy.lastName}
                </p>
              </div>
            )}
            <div>
              <p className="text-xs text-muted-foreground mb-1">Created</p>
              <p className="font-medium">{formatDate(invoice.created_at)}</p>
            </div>
            {Number(invoice.balance || 0) > 0 && (
              <Button size="sm" className="w-full" onClick={handleOpenPaymentDialog}>
                <CreditCard className="h-4 w-4 mr-1" />
                Apply Payment
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Line Items */}
      <div className="bg-card rounded-lg border">
        <div className="p-4 border-b flex items-center justify-between">
          <h3 className="font-semibold">Line Items</h3>
          <Button size="sm" onClick={() => handleOpenLineItemDialog()}>
            <Plus className="h-4 w-4 mr-1" />
            Add Line Item
          </Button>
        </div>
        {lineItems.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-24 text-muted-foreground">
            <FileText className="h-10 w-10 mb-2 opacity-50" />
            <p>No line items</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Description</TableHead>
                <TableHead className="text-right">Qty</TableHead>
                <TableHead>UOM</TableHead>
                <TableHead className="text-right">Unit Price</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead className="w-[60px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {lineItems.map((item) => (
                <TableRow key={item.id} className="group">
                  <TableCell>{item.description}</TableCell>
                  <TableCell className="text-right">{item.quantity}</TableCell>
                  <TableCell>{item.uom}</TableCell>
                  <TableCell className="text-right">{formatCurrency(item.unit_price)}</TableCell>
                  <TableCell className="text-right font-medium">{formatCurrency(item.line_total)}</TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 opacity-0 group-hover:opacity-100">
                          <MoreHorizontal className="h-3.5 w-3.5" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleOpenLineItemDialog(item)}>
                          <Pencil className="h-4 w-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="text-destructive"
                          onClick={() => deleteLineItemMutation.mutate(item.id)}
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Remove
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Payments */}
      {payments.length > 0 && (
        <div className="bg-card rounded-lg border">
          <div className="p-4 border-b">
            <h3 className="font-semibold">Payments</h3>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Method</TableHead>
                <TableHead>Reference</TableHead>
                <TableHead className="text-right">Amount</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {payments.map((payment) => (
                <TableRow key={payment.id}>
                  <TableCell>{formatDate(payment.payment_date)}</TableCell>
                  <TableCell>{payment.payment_method || '—'}</TableCell>
                  <TableCell>{payment.reference_number || '—'}</TableCell>
                  <TableCell className="text-right font-medium">{formatCurrency(payment.applied_amount)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Notes */}
      {invoice.notes && (
        <div className="bg-card rounded-lg border p-4">
          <h3 className="font-semibold mb-2">Notes</h3>
          <p className="text-sm whitespace-pre-wrap">{invoice.notes}</p>
        </div>
      )}

      {/* Dialogs */}
      <FormDrawer
        open={editOpen}
        onOpenChange={setEditOpen}
        title="Edit Invoice"
        description="Update the invoice information below."
        formId="edit-invoice"
        isPending={updateMutation.isPending}
        submitLabel="Save"
        size="md"
        data-testid="drawer-edit-invoice"
      >
        <Form {...form}>
          <form
            id="edit-invoice"
            onSubmit={form.handleSubmit((data) => updateMutation.mutate(data))}
            className="space-y-6"
          >
            <FormField
              control={form.control}
              name="status"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Status</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger variant="flat">
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="sent">Sent</SelectItem>
                      <SelectItem value="overdue">Overdue</SelectItem>
                      <SelectItem value="partially_paid">Partially Paid</SelectItem>
                      <SelectItem value="paid">Paid</SelectItem>
                      <SelectItem value="voided">Voided</SelectItem>
                      <SelectItem value="closed">Closed</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="dueDate"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>Due Date</FormLabel>
                  <Popover>
                    <PopoverTrigger asChild>
                      <FormControl>
                        <Button variant="outline" className={cn('pl-3 text-left font-normal', !field.value && 'text-muted-foreground')}>
                          {field.value ? formatDate(field.value.toISOString()) : 'Pick a date'}
                          <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar mode="single" selected={field.value || undefined} onSelect={field.onChange} />
                    </PopoverContent>
                  </Popover>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="paymentTerms"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Payment Terms</FormLabel>
                  <FormControl>
                    <Input variant="flat" {...field} placeholder="e.g., Net 30" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes</FormLabel>
                  <FormControl>
                    <Textarea variant="flat" {...field} rows={3} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </form>
        </Form>
      </FormDrawer>

      <FormDrawer
        open={lineItemDialogOpen}
        onOpenChange={(open) => {
          setLineItemDialogOpen(open);
          if (!open) setEditingLineItem(null);
        }}
        title={editingLineItem ? 'Edit Line Item' : 'Add Line Item'}
        description={editingLineItem ? 'Update the line item details.' : 'Add a new line item to this invoice.'}
        formId="invoice-line-item"
        isPending={lineItemMutation.isPending}
        submitLabel={editingLineItem ? 'Update' : 'Add'}
        size="md"
        data-testid="drawer-invoice-line-item"
      >
        <Form {...lineItemForm}>
          <form
            id="invoice-line-item"
            onSubmit={lineItemForm.handleSubmit((data) => lineItemMutation.mutate(data))}
            className="space-y-6"
          >
            <FormField
              control={lineItemForm.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Input variant="flat" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="grid grid-cols-2 gap-3">
              <FormField
                control={lineItemForm.control}
                name="quantity"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Quantity</FormLabel>
                    <FormControl>
                      <Input variant="flat" type="number" step="0.01" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={lineItemForm.control}
                name="uom"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>UOM</FormLabel>
                    <FormControl>
                      <Input variant="flat" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={lineItemForm.control}
              name="unitPrice"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Unit Price</FormLabel>
                  <FormControl>
                    <Input variant="flat" type="number" step="0.01" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </form>
        </Form>
      </FormDrawer>

      <FormDrawer
        open={paymentDialogOpen}
        onOpenChange={setPaymentDialogOpen}
        title="Apply Payment"
        description="Record a payment against this invoice."
        formId="apply-payment"
        isPending={paymentMutation.isPending}
        submitLabel="Apply Payment"
        size="md"
        data-testid="drawer-apply-payment"
      >
        <Form {...paymentForm}>
          <form
            id="apply-payment"
            onSubmit={paymentForm.handleSubmit((data) => paymentMutation.mutate(data))}
            className="space-y-6"
          >
            <FormField
              control={paymentForm.control}
              name="paymentDate"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>Payment Date</FormLabel>
                  <Popover>
                    <PopoverTrigger asChild>
                      <FormControl>
                        <Button variant="outline" className={cn('pl-3 text-left font-normal', !field.value && 'text-muted-foreground')}>
                          {field.value ? formatDate(field.value.toISOString()) : 'Pick a date'}
                          <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar mode="single" selected={field.value} onSelect={field.onChange} />
                    </PopoverContent>
                  </Popover>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={paymentForm.control}
              name="amount"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Amount</FormLabel>
                  <FormControl>
                    <Input variant="flat" type="number" step="0.01" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={paymentForm.control}
              name="paymentMethod"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Payment Method</FormLabel>
                  <FormControl>
                    <Input variant="flat" {...field} placeholder="e.g., Check, Wire Transfer" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={paymentForm.control}
              name="referenceNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Reference Number</FormLabel>
                  <FormControl>
                    <Input variant="flat" {...field} placeholder="e.g., Check #1234" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </form>
        </Form>
      </FormDrawer>

      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Invoice</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete invoice {invoice.invoice_number}? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={() => deleteMutation.mutate()} disabled={deleteMutation.isPending}>
              {deleteMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={archiveDialogOpen} onOpenChange={setArchiveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Archive Invoice</DialogTitle>
            <DialogDescription>
              Archive {invoice.invoice_number}? It will be hidden from default lists. You can restore it later.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setArchiveDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={() => archiveMutation.mutate()} disabled={archiveMutation.isPending}>
              {archiveMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Archive
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
